import { styled, alpha, type Theme } from '@mui/material/styles';
import { getTableRowHoverOverlay } from '../../../../styles/theme/tableRowOverlays';
import { ACCENT_COLOR } from '../../../../styles/theme/accentFieldSx';

/** Estilos MUI Select cuando el flag está apagado (valor heredado, solo lectura). */
export const inheritedSelectSx = (theme: Theme) => ({
  '&.Mui-disabled': {
    opacity: 1,
  },
  '& .MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.06)
        : theme.palette.background.paper,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  },
  '& .MuiSelect-select.Mui-disabled': {
    color: theme.palette.text.disabled,
    WebkitTextFillColor: theme.palette.text.disabled,
  },
});

// Container styles
export const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}));

export const EmptyState = styled('div')(({ theme }) => ({
  textAlign: 'center',
  padding: '32px 0',
}));

export const EmptyStateText = styled('p')(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

// Línea chica de estadísticas, sin caja — vive debajo de la barra de agregar.
export const SummaryStats = styled('p')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '12px',
  margin: 0,
}));

// --- Barra "Agregar producto" --- (sin caja/borde propios: controles sueltos sobre el fondo)
// Buscador+cantidad arriba, botón debajo, en su propia fila.
export const AddBarCard = styled('div')(({ theme }) => ({
  marginBottom: '10px',
}));

export const AddBarFormArea = styled('div')(({ theme }) => ({}));

// Texto + borde lila (ACCENT_COLOR). Reposo: borde punteado neutro, transparente (como un input
// sin foco). Hover: borde sólido + relleno lila suave (mismo mecanismo de antes) — con más
// redondeo, una sombra suave que da profundidad y un leve "levante" para que se sienta táctil,
// no plano. Al soltar/click, vuelve a su lugar (estado :active).
export const AddBarButton = styled('button')(({ theme }) => ({
  padding: '10px 16px',
  background: 'transparent',
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: '10px',
  color: ACCENT_COLOR,
  fontWeight: 700,
  fontSize: '15px',
  cursor: 'pointer',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  whiteSpace: 'nowrap',
  '&:hover:not(:disabled)': {
    borderStyle: 'solid',
    borderColor: ACCENT_COLOR,
    backgroundColor: alpha(ACCENT_COLOR, 0.12),
    boxShadow: `0 4px 12px ${alpha(ACCENT_COLOR, 0.22)}`,
    transform: 'translateY(-1px)',
  },
  '&:active:not(:disabled)': {
    transform: 'translateY(0)',
    boxShadow: 'none',
  },
  '&:disabled': {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
}));

// Product groups container
export const ProductGroupsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}));

// Product card styles
export const ProductCard = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
}));

export const ProductHeader = styled('div')<{ gradientType: 'blue' | 'green' }>(
  ({ theme, gradientType }) => ({
    padding: '12px 14px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    '&:hover': {
      opacity: 0.9,
    },
    ...(gradientType === 'blue'
      ? {
          background:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.12)
              : 'linear-gradient(to right, #eff6ff, #ecfeff)',
        }
      : {
          background:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.success.main, 0.12)
              : 'linear-gradient(to right, #f0fdf4, #ecfdf5)',
        }),
  }),
);

export const ProductHeaderContent = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '8px',
}));

export const ProductInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

export const ProductIcon = styled('span')<{ colorType: 'blue' | 'green' }>(
  ({ theme, colorType }) => ({
    marginRight: '12px',
    ...(colorType === 'blue'
      ? {
          color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#2563eb',
        }
      : {
          color: theme.palette.mode === 'dark' ? theme.palette.success.light : '#059669',
        }),
  }),
);

export const ProductDetails = styled('div')(({ theme }) => ({}));

export const ProductName = styled('h3')(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: '15px',
  lineHeight: 1.3,
}));

export const ProductCode = styled('p')(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.text.secondary,
  lineHeight: 1.3,
}));

export const ProductActions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '8px',
}));

export const HeaderQuantityField = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  marginRight: '4px',
}));

export const HeaderQuantityLabel = styled('span')(({ theme }) => ({
  fontSize: '11px',
  color: theme.palette.text.secondary,
}));

export const HeaderQuantityInput = styled('input')(({ theme }) => ({
  width: '48px',
  padding: '3px 4px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 600,
  textAlign: 'center',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
}));

/** Botones － / ＋ junto al campo de cantidad: suman o restan una pieza puntual, sin confirmación
 *  (misma lógica que el basurero por fila — acción precisa y visible). */
export const HeaderQuantityStepButton = styled('button')<{ colorType: 'blue' | 'green' }>(
  ({ theme, colorType }) => ({
    width: '22px',
    height: '22px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    border: '1px solid',
    cursor: 'pointer',
    fontSize: '14px',
    lineHeight: 1,
    padding: 0,
    transition: 'all 0.2s',
    '&:disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
    ...(colorType === 'blue'
      ? theme.palette.mode === 'dark'
        ? {
            borderColor: alpha(theme.palette.primary.main, 0.55),
            color: theme.palette.primary.light,
            backgroundColor: 'transparent',
            '&:hover:not(:disabled)': {
              backgroundColor: alpha(theme.palette.primary.main, 0.18),
            },
          }
        : {
            borderColor: '#93c5fd',
            color: '#1d4ed8',
            backgroundColor: '#fff',
            '&:hover:not(:disabled)': {
              backgroundColor: '#dbeafe',
            },
          }
      : theme.palette.mode === 'dark'
        ? {
            borderColor: alpha(theme.palette.success.main, 0.55),
            color: theme.palette.success.light,
            backgroundColor: 'transparent',
            '&:hover:not(:disabled)': {
              backgroundColor: alpha(theme.palette.success.main, 0.18),
            },
          }
        : {
            borderColor: '#6ee7b7',
            color: '#047857',
            backgroundColor: '#fff',
            '&:hover:not(:disabled)': {
              backgroundColor: '#d1fae5',
            },
          }),
  }),
);

export const ResetButton = styled('button')<{ colorType: 'blue' | 'green' }>(
  ({ theme, colorType }) => ({
    padding: '2px 8px',
    fontSize: '11px',
    borderRadius: '4px',
    border: '1px solid',
    cursor: 'pointer',
    transition: 'all 0.2s',
    ...(colorType === 'blue'
      ? theme.palette.mode === 'dark'
        ? {
            borderColor: alpha(theme.palette.primary.main, 0.55),
            color: theme.palette.primary.light,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.18),
            },
          }
        : {
            borderColor: '#93c5fd',
            color: '#1d4ed8',
            '&:hover': {
              backgroundColor: '#dbeafe',
            },
          }
      : theme.palette.mode === 'dark'
        ? {
            borderColor: alpha(theme.palette.success.main, 0.55),
            color: theme.palette.success.light,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: alpha(theme.palette.success.main, 0.18),
            },
          }
        : {
            borderColor: '#6ee7b7',
            color: '#047857',
            '&:hover': {
              backgroundColor: '#d1fae5',
            },
          }),
  }),
);

export const SectionDeleteButton = styled('button')(({ theme }) => ({
  width: '26px',
  height: '26px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '6px',
  border: `1px solid ${theme.palette.error.main}`,
  color: theme.palette.error.main,
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: alpha(theme.palette.error.main, 0.12),
  },
}));

export const UnitPaginationBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
}));

export const UnitPaginationLabel = styled('span')(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  minWidth: '72px',
  textAlign: 'center',
}));

export const UnitPaginationButton = styled('button')<{ colorType: 'blue' | 'green' }>(
  ({ theme, colorType }) => ({
    width: '28px',
    height: '28px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    border: '1px solid',
    cursor: 'pointer',
    fontSize: '16px',
    lineHeight: 1,
    padding: 0,
    transition: 'all 0.2s',
    '&:disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
    ...(colorType === 'blue'
      ? theme.palette.mode === 'dark'
        ? {
            borderColor: alpha(theme.palette.primary.main, 0.55),
            color: theme.palette.primary.light,
            backgroundColor: 'transparent',
            '&:hover:not(:disabled)': {
              backgroundColor: alpha(theme.palette.primary.main, 0.18),
            },
          }
        : {
            borderColor: '#93c5fd',
            color: '#1d4ed8',
            backgroundColor: '#fff',
            '&:hover:not(:disabled)': {
              backgroundColor: '#dbeafe',
            },
          }
      : theme.palette.mode === 'dark'
        ? {
            borderColor: alpha(theme.palette.success.main, 0.55),
            color: theme.palette.success.light,
            backgroundColor: 'transparent',
            '&:hover:not(:disabled)': {
              backgroundColor: alpha(theme.palette.success.main, 0.18),
            },
          }
        : {
            borderColor: '#6ee7b7',
            color: '#047857',
            backgroundColor: '#fff',
            '&:hover:not(:disabled)': {
              backgroundColor: '#d1fae5',
            },
          }),
  }),
);

export const ExpandIcon = styled('span')<{ colorType: 'blue' | 'green' }>(
  ({ theme, colorType }) => ({
    ...(colorType === 'blue'
      ? {
          color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#2563eb',
        }
      : {
          color: theme.palette.mode === 'dark' ? theme.palette.success.light : '#059669',
        }),
  }),
);

// Table styles
export const TableContainer = styled('div')(({ theme }) => ({
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
}));

export const Table = styled('table')(({ theme }) => ({
  tableLayout: 'fixed',
  minWidth: '660px',
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
}));

export const TableHead = styled('thead')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
}));

export const TableRow = styled('tr')(({ theme }) => ({
  '&:last-child td': {
    borderBottom: 0,
  },
  transition: 'background 0.2s',
  '&:hover': {
    backgroundColor: `${getTableRowHoverOverlay(theme)} !important`,
    background: `${getTableRowHoverOverlay(theme)} !important`,
  },
}));

export const HeaderCell = styled('th')(({ theme }) => ({
  padding: `${theme.spacing(0.5)} ${theme.spacing(0.75)}`,
  textAlign: 'left',
  fontSize: '0.7rem',
  fontWeight: 600,
  color: theme.palette.text.secondary || '#6b7280',
  textTransform: 'none',
  letterSpacing: 0.2,
  borderBottom: `1px solid ${theme.palette.divider || '#e5e7eb'}`,
  whiteSpace: 'nowrap',
  backgroundColor: theme.palette.background.paper,
  lineHeight: 1.2,
}));

export const HeaderCellWithActions = styled(HeaderCell)(({ theme }) => ({
  textAlign: 'center',
  '& > div': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
  },
}));

export const StickyHeaderCell = styled(HeaderCell)(({ theme }) => ({
  position: 'sticky',
  left: 0,
  zIndex: 3,
  width: '30px',
  minWidth: '30px',
  textAlign: 'center',
  boxShadow: `1px 0 0 ${theme.palette.divider}`,
}));

export const StickyBodyCell = styled('td')(({ theme }) => ({
  position: 'sticky',
  left: 0,
  zIndex: 2,
  width: '30px',
  minWidth: '30px',
  textAlign: 'center',
  fontWeight: 600,
  fontSize: '0.8rem',
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  boxShadow: `1px 0 0 ${theme.palette.divider}`,
  whiteSpace: 'nowrap',
  'tr:hover &': {
    backgroundColor: getTableRowHoverOverlay(theme),
  },
}));

export const TableBody = styled('tbody')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  '& > tr': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: 'background 0.2s',
    '&:hover': {
      backgroundColor: `${getTableRowHoverOverlay(theme)} !important`,
      background: `${getTableRowHoverOverlay(theme)} !important`,
    },
  },
}));

export const BodyCell = styled('td')(({ theme }) => ({
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
  whiteSpace: 'nowrap',
  fontSize: '0.8rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

export const RowDeleteButton = styled('button')(({ theme }) => ({
  width: '24px',
  height: '24px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  border: 'none',
  background: 'transparent',
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  transition: 'color 0.2s, background-color 0.2s',
  '&:hover': {
    color: theme.palette.error.main,
    backgroundColor: alpha(theme.palette.error.main, 0.1),
  },
}));

export const InputField = styled('input')(({ theme }) => ({
  width: '100%',
  padding: '4px 8px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '4px',
  fontSize: '12px',
  lineHeight: 1.3,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.06)
      : theme.palette.background.paper,
  color: theme.palette.text.primary,
  '&:focus': {
    outline: 'none',
    ring: '2px',
    ringColor: '#0891b2',
    borderColor: '#0891b2',
  },
  '&:disabled': {
    color: theme.palette.text.disabled,
    WebkitTextFillColor: theme.palette.text.disabled,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.06)
        : theme.palette.background.paper,
    borderColor: theme.palette.divider,
    cursor: 'not-allowed',
    opacity: 1,
  },
}));

export const SelectField = styled('div')(({ theme }) => ({
  width: '100%',
}));
